// Rebuilds list structure from the HTML Word puts on the clipboard.
//
// Word's HTML does not nest lists. A sub-list is emitted as a sibling of the
// item it belongs to (`</li><ol type=a>`), lists are closed and reopened
// around items Word chooses to write as plain paragraphs, and some items —
// in the Training Manual's own reference document, every level-3 item and
// everything from item 12 on — are not list markup at all but
// `<p class=MsoListParagraph…>` with the visible number baked in as text.
//
// The one reliable signal is the `mso-list: lN levelM lfoK` style Word puts
// on every item, real or fake: M is the depth, K identifies the list. TinyMCE's
// own Word filter strips that style before `paste_preprocess` runs, and its
// fake-list conversion cannot see the real lists around a fake item, so a
// level-3 item lands as a new top-level list. This module therefore runs on
// the raw clipboard HTML, ahead of the plugin, and rebuilds every list from
// the levels — after which TinyMCE's filter has only styling left to clean.

const LIST_STYLE = /mso-list\s*:\s*(l\d+)\s+level(\d+)(?:\s+(lfo\d+))?/i;
const IGNORE_STYLE = /mso-list\s*:\s*Ignore/i;
const ORDERED_MARKER = /^[(\[]?\s*(\d+|[a-z]{1,3}|[ivxlcdm]{1,7})\s*[.)\]]/i;

// Blocks that end a run of items: numbering continues past them via `start`.
const BREAKING_BLOCKS = {
    P: true, H1: true, H2: true, H3: true, H4: true, H5: true, H6: true,
    TABLE: true, BLOCKQUOTE: true, PRE: true, HR: true, IMG: true,
};

export function isWordListHtml(html) {
    return typeof html === 'string' && LIST_STYLE.test(html);
}

export function rebuildWordListsFromClipboard(html) {
    if (!isWordListHtml(html) || typeof DOMParser === 'undefined') return null;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const body = doc.body;
    if (!body) return null;

    const wrappers = [];
    const runs = [];
    const emptied = [];
    let run = null;
    let breakPending = false;

    const walker = doc.createTreeWalker(body, NodeFilter.SHOW_ELEMENT);
    for (let el = walker.nextNode(); el; el = walker.nextNode()) {
        if (el.tagName === 'OL' || el.tagName === 'UL') {
            wrappers.push(el);
            continue;
        }

        // Inline content of an item or paragraph is not a candidate itself.
        const enclosing = el.parentNode && el.parentNode.closest
            ? el.parentNode.closest('li, p, ol, ul')
            : null;
        if (enclosing && (enclosing.tagName === 'LI' || enclosing.tagName === 'P')) continue;

        const item = readItem(el);
        if (item) {
            // An item with nothing in it is a Word paragraph mark that was
            // numbered; it must not consume a number here.
            if (item.empty) { emptied.push(el); continue; }
            if (!run || breakPending) { run = []; runs.push(run); }
            breakPending = false;
            run.push(item);
            continue;
        }

        if (BREAKING_BLOCKS[el.tagName] && run) breakPending = true;
    }

    if (!runs.length) return null;

    const counters = {};
    runs.forEach(items => buildRun(doc, items, counters));

    runs.forEach(items => items.forEach(item => remove(item.el)));
    emptied.forEach(remove);
    wrappers.forEach(unwrap);
    Array.from(body.querySelectorAll('div')).forEach(div => {
        if (/^WordSection/i.test(div.className || '')) unwrap(div);
    });

    return body.innerHTML;
}

function readItem(el) {
    const tag = el.tagName;
    if (tag !== 'LI' && tag !== 'P') return null;

    const match = LIST_STYLE.exec(el.getAttribute('style') || '');
    if (!match && tag !== 'LI') return null;

    const wordList = tag === 'LI' ? el.closest('ol, ul') : null;
    const marker = takeMarker(el);
    const level = Math.max(1, match ? parseInt(match[2], 10) : nestingDepth(el));

    return {
        el,
        level,
        listId: match ? (match[3] || match[1]) : 'word-list',
        ordered: wordList ? wordList.tagName === 'OL' : ORDERED_MARKER.test(marker),
        startHint: wordList
            ? parseInt(wordList.getAttribute('start'), 10) || null
            : markerNumber(marker, level),
        empty: !hasContent(el),
    };
}

// Builds one nested tree for a consecutive run of items. `stack[d - 1]` is the
// list open at depth d; a shallower item pops back to its level, a deeper one
// opens a list inside the last item above it, so numbering at every level is
// left to the browser.
function buildRun(doc, items, counters) {
    const anchor = items[0].el;
    const stack = [];

    items.forEach(item => {
        stack.length = Math.min(stack.length, item.level);

        while (stack.length < item.level) {
            const opening = stack.length + 1;
            const isTarget = opening === item.level;
            const list = doc.createElement(isTarget && !item.ordered ? 'ul' : 'ol');

            if (!stack.length) {
                anchor.parentNode.insertBefore(list, anchor);
            } else {
                const parent = stack[stack.length - 1];
                const host = parent.lastElementChild || parent.appendChild(doc.createElement('li'));
                host.appendChild(list);
            }

            if (list.tagName === 'OL') {
                if (opening === 1) {
                    // A run interrupted by a paragraph resumes where the same
                    // Word list left off; a run that begins mid-list (a partial
                    // paste) starts where Word says it did. Written explicitly
                    // even at 1: a list that declares its start is a sequence
                    // of its own and is not chained onto its neighbours later.
                    const used = counters[item.listId] || 0;
                    const start = used > 0 ? used + 1 : (isTarget && item.startHint > 1 ? item.startHint : 1);
                    list.setAttribute('start', String(start));
                    counters[item.listId] = start - 1;
                } else if (isTarget && item.startHint > 1) {
                    list.setAttribute('start', String(item.startHint));
                }
            }

            stack.push(list);
        }

        const li = doc.createElement('li');
        // Kept so TinyMCE still recognises the paste as Word content and runs
        // its own clean-up; the class is stripped there, and again on render.
        li.setAttribute('class', item.el.getAttribute('class') || 'MsoNormal');
        moveContent(item.el, li);
        stack[item.level - 1].appendChild(li);

        if (item.level === 1) counters[item.listId] = (counters[item.listId] || 0) + 1;
    });
}

// The literal marker Word wrote ("10.", "a.", a bullet glyph) lives in a
// `mso-list:Ignore` span; removing it is what lets the browser do the numbering.
function takeMarker(el) {
    let text = '';
    Array.from(el.querySelectorAll('span')).forEach(span => {
        if (!IGNORE_STYLE.test(span.getAttribute('style') || '')) return;
        if (!el.contains(span)) return;
        if (!text) text = (span.textContent || '').replace(/ /g, ' ').trim();
        span.parentNode.removeChild(span);
    });
    return text;
}

// Word's default numbering cycles decimal / letter / roman by level, which
// settles whether "i." or "v." is a letter or a numeral.
function markerNumber(marker, level) {
    const match = /^[(\[]?\s*(\d+|[a-z]{1,4})\s*[.)\]]/i.exec(marker || '');
    if (!match) return null;
    const token = match[1].toLowerCase();
    if (/^\d+$/.test(token)) return parseInt(token, 10);
    if (level % 3 === 0 && /^[ivxlcdm]+$/.test(token)) return romanToInt(token);
    return token.split('').reduce((n, ch) => n * 26 + (ch.charCodeAt(0) - 96), 0);
}

function romanToInt(roman) {
    const values = { i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000 };
    let total = 0;
    for (let i = 0; i < roman.length; i++) {
        const current = values[roman[i]];
        const next = values[roman[i + 1]] || 0;
        total += current < next ? -current : current;
    }
    return total || null;
}

function nestingDepth(el) {
    let depth = 0;
    for (let node = el.parentNode; node; node = node.parentNode) {
        if (node.tagName === 'OL' || node.tagName === 'UL') depth++;
    }
    return depth;
}

function hasContent(el) {
    if (el.querySelector('img, table, video, iframe')) return true;
    return (el.textContent || '').replace(/ /g, ' ').trim() !== '';
}

function moveContent(from, to) {
    Array.from(from.childNodes).forEach(node => {
        // Word's `<![if !supportLists]>` guards parse as comments.
        if (node.nodeType === 8) return;
        if (node.nodeType === 1 && (node.tagName === 'OL' || node.tagName === 'UL')) return;
        to.appendChild(node);
    });
}

function unwrap(el) {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
}

function remove(el) {
    if (el.parentNode) el.parentNode.removeChild(el);
}

export default rebuildWordListsFromClipboard;
